import { DEFAULT_NAVIGATION_ICON_BY_CATALOG_KEY } from '@/constants/navigationIcons';

export function normalizeFeatureLocale(locale = 'en') {
  return String(locale).toLowerCase().startsWith('de') ? 'de' : 'en';
}

/** Same locale source as CustomerSidebar nav labels and CustomerLayout header. */
export function resolveCustomerAppLocale(store, i18nLocale = 'en') {
  const preferred = store?.getters?.getUser?.preferredLanguage
    ?? store?.state?.auth?.user?.preferredLanguage
    ?? store?.state?.auth?.lang
    ?? i18nLocale;
  return normalizeFeatureLocale(preferred);
}

/** Feature Manager label resolution: prefer requested locale, then the other. */
export function labelFromLocalizedMap(names, locale = 'en') {
  if (!names || typeof names !== 'object') return '';
  const loc = normalizeFeatureLocale(locale);
  const primary = (names[loc] || '').trim();
  if (primary) return primary;
  const secondary = loc === 'de' ? (names.en || '').trim() : (names.de || '').trim();
  return secondary || '';
}

/**
 * Display name for an assessment schema in dropdowns (Create Activity EE, admin EE create).
 * Prefers representationProperties.assessmentName for the active locale.
 */
export function assessmentSchemaDisplayName(schema, locale = 'en', fallback = 'Unnamed Schema') {
  const fromName = labelFromLocalizedMap(
    schema?.representationProperties?.assessmentName,
    locale,
  );
  return fromName || schema?.type || fallback;
}

export function getFeatureDisplayNameFromMap(featureDisplayByKey, catalogKey, locale = 'en') {
  const label = labelFromLocalizedMap(featureDisplayByKey?.[catalogKey], locale);
  return label || null;
}

export function iconForNavItem(item) {
  if (item?.icon) return item.icon;
  return DEFAULT_NAVIGATION_ICON_BY_CATALOG_KEY[item?.catalogKey] || 'pi pi-circle';
}

export function labelForNavItem(item, locale = 'en') {
  if (!item?.label) return '';
  return labelFromLocalizedMap(item.label, locale);
}

/**
 * Build top-level nav items and children map from flat customerNavigation array.
 */
export function buildNavigationTree(items) {
  if (!Array.isArray(items) || !items.length) {
    return { topLevel: [], childrenByParent: {} };
  }
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  const topLevel = sorted.filter((i) => !i.parentCatalogKey);
  const childrenByParent = {};
  sorted
    .filter((i) => i.parentCatalogKey)
    .forEach((child) => {
      const key = child.parentCatalogKey;
      if (!childrenByParent[key]) childrenByParent[key] = [];
      childrenByParent[key].push(child);
    });
  Object.keys(childrenByParent).forEach((k) => {
    childrenByParent[k].sort((a, b) => a.sortOrder - b.sortOrder);
  });
  return { topLevel, childrenByParent };
}

export function findNavItemByRoute(items, path) {
  if (!Array.isArray(items) || !path) return null;
  const normalized = String(path).split('?')[0];
  const links = items.filter((i) => i.itemKind === 'link' && typeof i.route === 'string');
  const matches = links.filter((i) => {
    const route = i.route;
    if (route === '/') return normalized === '/' || normalized === '';
    return normalized === route || normalized.startsWith(`${route}/`);
  });
  if (!matches.length) return null;
  matches.sort((a, b) => (b.route?.length || 0) - (a.route?.length || 0));
  return matches[0];
}

export function findNavItemByCatalogKey(items, catalogKey) {
  if (!Array.isArray(items) || !catalogKey) return null;
  return items.find((item) => item.catalogKey === catalogKey) || null;
}

function getCustomerNavigationList(store) {
  const fromGetter = store?.getters?.getCustomerNavigation;
  if (Array.isArray(fromGetter)) return fromGetter;
  return store?.state?.auth?.customerNavigation ?? [];
}

function getFeatureDisplayByKey(store) {
  const fromGetter = store?.getters?.getFeatureDisplayByKey;
  if (fromGetter && typeof fromGetter === 'object') return fromGetter;
  return store?.state?.auth?.featureDisplayByKey ?? {};
}

/**
 * Page title from Features Manager (same labels as the customer sidebar menu).
 * @returns {string|null}
 */
export function resolveFeatureManagerTitle(store, { path, catalogKey }, locale = 'en') {
  const loc = normalizeFeatureLocale(locale);
  const nav = getCustomerNavigationList(store);
  let navItem = findNavItemByRoute(nav, path);
  const resolvedCatalogKey = catalogKey || navItem?.catalogKey || null;

  if (!navItem && resolvedCatalogKey) {
    navItem = findNavItemByCatalogKey(nav, resolvedCatalogKey);
  }

  if (navItem?.label) {
    const label = labelForNavItem(navItem, loc);
    if (label) return label;
  }

  if (resolvedCatalogKey) {
    const label = labelFromLocalizedMap(getFeatureDisplayByKey(store)[resolvedCatalogKey], loc);
    if (label) return label;
  }

  return null;
}
